import { number, object, oneOfType, string } from "prop-types";
import React, { useRef, useState } from "react";

import styled from "styled-components/macro";

import { useNetworkMetadata, useUserData } from "Flocknote/apiHooks";

import { useOpenSacramentModal } from "Boards/Cells/Dynamic/SacramentFields/useOpenSacramentModal";

import { FnrPopover } from "UI/FnrPopover2";

import {
  cellContainerStyles,
  focusVisibleCellStyles,
} from "../cellContainerStyles";

SacramentChurch.propTypes = {
  value: oneOfType([object, string]),
  entityID: number,
  extendedFieldData: object,
};

export default function SacramentChurch({
  value,
  entityID,
  extendedFieldData,
}) {
  const wrapperRef = useRef();
  const [networkMetadata] = useNetworkMetadata();
  const [{ canEditSacraments }] = useUserData();
  const { networkLogo } = networkMetadata;

  const [showPopover, setShowPopover] = useState(false);

  const church = value?.church;
  const atCurrentChurch = value?.atCurrentChurch;

  // address not destructured because value can be an empty array when no address has been saved for the church
  const address = value?.address;
  const addr1 = address?.addr1;
  const city = address?.city;
  const state = address?.state;
  const zip = address?.zip;
  const country = address?.country;

  const hasAddress = addr1 || city || state || zip || country;
  const hasAddr1 = Boolean(addr1);
  const hasCity = Boolean(city);
  const hasState = Boolean(state);
  const hasZip = Boolean(zip);
  const hasCountry = Boolean(country);

  const { openSacramentModal } = useOpenSacramentModal({
    targetUserID: entityID,
    defaultFieldTypeInstanceMapping:
      extendedFieldData?.defaultFieldTypeInstanceMapping,
    networkFieldID: extendedFieldData?.parentNetworkFieldID,
  });

  const handleClick = () => {
    if (!canEditSacraments) return;
    openSacramentModal();
  };

  const handleMouseEnter = () => {
    if (atCurrentChurch) return;
    setShowPopover(true);
  };

  const handleMouseLeave = () => {
    if (atCurrentChurch) return;
    setShowPopover(false);
  };

  return (
    <>
      <Wrapper
        ref={wrapperRef}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        $canEditSacraments={canEditSacraments}
        role="combobox"
        aria-expanded={showPopover}
        aria-haspopup="dialog"
      >
        <Content>
          {atCurrentChurch && <img src={networkLogo} alt="network logo" />}
          {!atCurrentChurch && hasAddress && (
            <i className="fad fa-map-marker" />
          )}
          <span>{church}</span>
        </Content>

        {!atCurrentChurch && hasAddress && (
          <FnrPopover
            buttonRef={wrapperRef}
            show={showPopover}
            onShouldClose={() => {
              setShowPopover(false);
            }}
          >
            <PopoverContent>
              <Church>
                <span>{church}</span>
              </Church>
              {hasAddress ? (
                <div>
                  {hasAddr1 && <div>{addr1}</div>}
                  <div>
                    {hasCity && city}
                    {hasCity && (hasState || hasZip) && ", "}
                    {hasState && `${state} `}
                    {hasZip && zip}
                  </div>
                  {hasCountry && <div>{country}</div>}
                </div>
              ) : null}
            </PopoverContent>
          </FnrPopover>
        )}
      </Wrapper>
    </>
  );
}

const Wrapper = styled.div`
  ${focusVisibleCellStyles};
  box-sizing: border-box;
  cursor: ${(p) => (p.$canEditSacraments ? "pointer" : "default")};
  position: relative;
  overflow: hidden;
`;

const Content = styled.div`
  ${cellContainerStyles};

  display: flex;
  align-items: center;
  gap: 6px;

  & img {
    width: 13px;
    height: 13px;
  }
`;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.25rem;
`;
const Church = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
`;
